import { ClerkLoaded, ClerkProvider } from "@clerk/clerk-expo";
import { Stack } from "expo-router";
import * as SecureStore from "expo-secure-store";
import { StatusBar } from "expo-status-bar";

import type { ThemeProp } from "react-native-paper/lib/typescript/types";

/* -----------------UI--------------- */
import { PaperProvider } from "react-native-paper";

import theme from "@configs/theme";
import { useThemeContext } from "@contexts/ThemeContext";
import { ThemeProvider } from "@react-navigation/native";
/* -----------------Helpers & Hooks--------------- */
import useCachedResources from "../src/hooks/useCachedResources";

/* -----------------Child components--------------- */
import { ThemeContextProvider } from "@contexts/ThemeContext";
import { AuthProvider } from "@features/auth/contexts/AuthContext";
import { ConsentProvider } from "@features/auth/contexts/ConsentProvider";
import { ExamContextProvider } from "@features/exam/contexts/ExamContext";
import { buildType } from "@helpers/expo-extras";
import { identifyUser, initializeAnalytics } from "analytics";
import { GestureHandlerRootHOC } from "components/GestureHandlerRootHOC";

import { isWeb } from "@helpers/platform";
import { applyGlobalPolyfills } from "@memorang/helpers/src/applyGlobalPolyfills";
import { CommandBarProviderWrapper } from "components/CommandBarProviderWrapper";
import Head from "expo-router/head";

import { HapticProvider } from "@contexts/HapticContexts";
import ReactQueryProvider from "@contexts/ReactQueryContext";
import { BottomSheetModalProvider } from "@gorhom/bottom-sheet";
import { getClerkPublishableKey } from "@helpers/find-onboarding";
import { initSentry } from "@helpers/lib/sentry";
import { useAppStore } from "@hooks/useAppStore";
import useClerkUser from "@hooks/useClerkUser";
import useExams from "@hooks/useExams";
import { useProductDetails } from "@hooks/useProduct";
import { useProductMetadata } from "@hooks/useProduct";
import { TTSProvider } from "@memorang/features/src/TTS/TTSContext";
import Loader from "@memorang/ui/src/components/Loader";
import { ToastProvider } from "@memorang/ui/src/components/Toast";
import * as SentryNative from "@sentry/react-native";
import { LogBox } from "react-native";
import { OverlayProvider } from "./providers/OverlayProvider";
import VersionCheckerProvider from "./providers/VersionChecker";

const enabled = false;

if (enabled) {
	LogBox.ignoreAllLogs();
}

applyGlobalPolyfills();
initSentry();
initializeAnalytics();

const tokenCache = {
	async getToken(key: string) {
		try {
			const item = await SecureStore.getItemAsync(key);
			return item;
		} catch (error) {
			console.error("SecureStore get item error: ", error);
			await SecureStore.deleteItemAsync(key);
			return null;
		}
	},
	async saveToken(key: string, value: string) {
		try {
			return await SecureStore.setItemAsync(key, value);
		} catch (_err) {
			return;
		}
	},
};

const RenderRoot = () => {
	const { isDarkMode } = useThemeContext();

	const { isLoaded } = useClerkUser();

	const appId = useAppStore((store) => store.app?.id);
	const org = useAppStore((store) => store.companyName);
	const tenantId = useAppStore((store) => store.tenant?.id);
	const voiceId = useAppStore((state) => state.app?.elevenLabs?.voiceId); // TODO: change to generic
	const appName = useAppStore((state) => state.app?.name);

	const finalTheme = theme(isDarkMode);
	useProductMetadata("STUDY_PACK");
	useProductMetadata("PRACTICE_TEST");
	useProductDetails("PRACTICE_TEST");
	useProductDetails("STUDY_PACK");
	useExams();

	if (!isLoaded) {
		return <Loader />;
	}
	return (
		<PaperProvider theme={finalTheme as unknown as ThemeProp}>
			<ThemeProvider
				value={{
					...finalTheme,
					// TODO: remove if react native paper gets a major version bump
					fonts: {
						...finalTheme.typography.fonts,
						heavy: finalTheme.typography.fonts.extraBold,
						// biome-ignore lint/correctness/noUndeclaredVariables: available in global namespace
					} as ReactNavigation.Theme["fonts"],
				}}
			>
				<StatusBar
					key={isDarkMode ? "dark" : "light"}
					style={isDarkMode ? "light" : "dark"}
				/>
				<ToastProvider>
					<VersionCheckerProvider>
						<HapticProvider>
							<TTSProvider
								org={org}
								tenantId={tenantId}
								appId={appId}
								appName={appName}
								voiceId={voiceId ?? ""}
							>
								<BottomSheetModalProvider>
									<OverlayProvider>
										<CommandBarProviderWrapper>
											<Stack
												screenOptions={{
													headerShown: false,
												}}
											/>
										</CommandBarProviderWrapper>
									</OverlayProvider>
								</BottomSheetModalProvider>
							</TTSProvider>
						</HapticProvider>
					</VersionCheckerProvider>
				</ToastProvider>
			</ThemeProvider>
		</PaperProvider>
	);
};

function App() {
	const { isLoadingComplete, isDarkMode } = useCachedResources();

	const appId = useAppStore((store) => store.app.id);

	if (!isLoadingComplete) {
		return null;
	}
	const clerkPublishableKey = getClerkPublishableKey();

	return (
		<ClerkProvider tokenCache={tokenCache} publishableKey={clerkPublishableKey}>
			<ClerkLoaded>
				{isWeb && (
					<Head>
						<link
							rel="shortcut icon"
							href={`https://services.memorang.com/storage/v1/object/public/memorang-assets/icons/${buildType}/icon.png`}
						/>
					</Head>
				)}
				<GestureHandlerRootHOC>
					<ThemeContextProvider initIsDarkMode={isDarkMode}>
						<ReactQueryProvider>
							<AuthProvider
								app={buildType}
								identifyUser={identifyUser}
								appId={appId}
							>
								<ExamContextProvider>
									<ConsentProvider>
										<RenderRoot />
									</ConsentProvider>
								</ExamContextProvider>
							</AuthProvider>
						</ReactQueryProvider>
					</ThemeContextProvider>
				</GestureHandlerRootHOC>
			</ClerkLoaded>
		</ClerkProvider>
	);
}
export default isWeb ? App : SentryNative.wrap(App);
